import { motion } from "framer-motion";
import { Wind, Stethoscope, HeartPulse, Droplets, CalendarCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const REVIEWS = [
  {
    icon: Wind,
    title: "Asthma Review",
    service: "Asthma Review",
    intro: "Understand how well your asthma is controlled — and whether anything is making your symptoms harder to manage.",
    points: [
      "Symptom frequency and reliever use",
      "Night-time symptoms and activity limits",
      "Recent flare-ups or urgent care",
      "Preventer use, inhaler & spacer technique",
      "Triggers and your Asthma Action Plan",
    ],
    outro: "Leave confident using your treatment, spotting changes early and knowing when to seek extra care.",
  },
  {
    icon: Stethoscope,
    title: "COPD Review",
    service: "COPD Consult",
    intro: "Time to talk through your breathing, your medicines and how COPD is affecting everyday life.",
    points: [
      "Breathlessness, cough or sputum changes",
      "Recent flare-ups, infections or hospital visits",
      "Inhaler selection and technique",
      "Smoking or vaping support",
      "Vaccination, activity & pulmonary rehab options",
      "Your COPD Action Plan",
    ],
    outro: "We'll help you use medicines effectively and spot deterioration earlier — with your GP looped in when needed.",
  },
  {
    icon: HeartPulse,
    title: "Heart Health Review",
    service: "Heart Health Consult",
    intro: "Build a clearer picture of the factors that may affect your future cardiovascular health.",
    points: [
      "Blood pressure and relevant measurements",
      "Your cholesterol or blood glucose results",
      "Smoking/vaping, family & medical history",
      "Current medicines and how they're used",
      "Nutrition, activity, weight and alcohol",
      "Your overall cardiovascular risk",
    ],
    outro: "We'll explain what it all means and agree realistic next steps — with GP follow-up if a formal risk check is needed.",
  },
  {
    icon: Droplets,
    title: "Diabetes Review",
    service: "Diabetic Management Consult",
    intro: "Individual support for people living with diabetes — and those who may be at risk of type 2 diabetes.",
    points: [
      "Getting the best benefit from your medicines",
      "Blood glucose monitoring & your results",
      "Meters, sensors and insulin pen technique",
      "Managing low and high blood glucose",
      "Nutrition, activity and routine checks",
      "Diabetes risk assessment if undiagnosed",
    ],
    outro: "Improve your confidence, catch concerns early and keep your GP and diabetes team in the loop.",
  },
];

const bookReview = (service) => {
  window.dispatchEvent(new CustomEvent("preselect-service", { detail: service }));
  document.querySelector("#book")?.scrollIntoView({ behavior: "smooth" });
};

export default function ChronicCare() {
  return (
    <section id="chronic-care" data-testid="chronic-care-section" className="scroll-mt-24 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-pink" data-testid="chronic-eyebrow">Chronic Care Reviews</p>
          <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-4xl" data-testid="chronic-heading">
            Living well with long-term conditions
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="chronic-intro">
            Living with a long-term health condition can involve more than simply taking medication. Symptoms,
            treatments, devices and health goals can change over time, and it is not always easy to know whether
            everything is working as well as it could. Our pharmacist-led Chronic Care Reviews provide a convenient
            opportunity to discuss your condition, understand your medicines and identify practical ways to support
            your health.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Your review will be tailored to you — symptoms, medicines, treatment goals, monitoring results, lifestyle
            factors and any difficulties you may be experiencing. Where appropriate, our pharmacists can perform
            relevant health checks, review how medicines or devices are being used, and help identify when follow-up
            with your GP would be beneficial. These consultations complement — not replace — the care provided by
            your GP or specialist.
          </p>
        </div>

        <Accordion type="single" collapsible className="mt-12 grid gap-4 lg:grid-cols-2" data-testid="chronic-reviews">
          {REVIEWS.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: (i % 2) * 0.08 }}
            >
              <AccordionItem
                value={r.title}
                className="overflow-hidden rounded-2xl border border-border/60 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.04)]"
                data-testid={`chronic-box-${r.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <AccordionTrigger
                  className="px-6 py-5 hover:no-underline [&[data-state=open]]:bg-brand-blush/40"
                  data-testid={`chronic-toggle-${r.title.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <span className="flex items-center gap-4 text-left">
                    <span className="inline-flex shrink-0 rounded-xl bg-brand-blush p-3 text-brand-pink">
                      <r.icon className="h-6 w-6" />
                    </span>
                    <span>
                      <span className="block font-heading text-lg font-bold">{r.title}</span>
                      <span className="mt-0.5 block text-xs font-medium text-muted-foreground">Pharmacist-led consult · click to read what's included</span>
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6" data-testid={`chronic-content-${r.title.toLowerCase().replace(/\s/g, "-")}`}>
                  <p className="text-sm leading-relaxed text-muted-foreground">{r.intro}</p>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-brand-pink">What we may cover</p>
                  <ul className="mt-2 space-y-2" data-testid={`chronic-points-${r.title.toLowerCase().replace(/\s/g, "-")}`}>
                    {r.points.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-sm text-foreground/85">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-green" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-sm italic leading-relaxed text-muted-foreground">{r.outro}</p>
                  <button
                    onClick={() => bookReview(r.service)}
                    data-testid={`chronic-book-${r.title.toLowerCase().replace(/\s/g, "-")}`}
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-pink px-5 py-2.5 font-heading text-sm font-semibold text-white transition-colors duration-200 hover:bg-brand-pinkdark"
                  >
                    <CalendarCheck className="h-4 w-4" /> Book this review
                  </button>
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
